import { type CreatePackageImageRequest, type PackageDetail } from '@utazzo/shared';
import { UzFormField } from '@utazzo/ui';
import React from 'react';

import { useUpdatePackage } from '../use-packages';

import { GalleryEditor } from './GalleryEditor';

import { FormSection } from '@/components/form-section';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

interface ImagesTabProps {
  /** Live (edit) mode: the existing package. */
  pkg?: PackageDetail | null;
  /**
   * Create mode (when `pkg` is absent): staged gallery images held in the
   * parent. Hero images bind to the surrounding form via UzFormField, so this
   * component must be rendered inside the page's <UzForm> in create mode.
   */
  galleryDraft?: CreatePackageImageRequest[];
  onGalleryDraftChange?: (items: CreatePackageImageRequest[]) => void;
}

export const ImagesTab = ({
  pkg,
  galleryDraft,
  onGalleryDraftChange,
}: ImagesTabProps): React.JSX.Element => {
  const updatePkg = useUpdatePackage();
  const pending = updatePkg.isPending;

  const setMedia = (
    key: 'mainImageId' | 'bannerImageId' | 'bannerVideoId',
    value: string | null,
  ): void => {
    if (!pkg) return;
    void updatePkg.mutateAsync({ id: pkg.id, payload: { [key]: value } });
  };

  return (
    <div className="space-y-10 pb-10">
      <FormSection title="Hero media" description="Shown at the top of the public package page.">
        {pkg ? (
          <>
            <MediaDropField
              label="Main image"
              value={pkg.mainImageId}
              onChange={(v) => setMedia('mainImageId', v)}
              kind="image"
              folder="packages"
              variant="square"
              disabled={pending}
            />
            <MediaDropField
              label="Banner image"
              value={pkg.bannerImageId}
              onChange={(v) => setMedia('bannerImageId', v)}
              kind="image"
              folder="packages"
              variant="banner"
              disabled={pending}
            />
            <MediaDropField
              label="Banner video"
              value={pkg.bannerVideoId}
              onChange={(v) => setMedia('bannerVideoId', v)}
              kind="video"
              folder="packages"
              variant="banner"
              disabled={pending}
            />
          </>
        ) : (
          <>
            <UzFormField name="mainImageId">
              {({ field }) => (
                <MediaDropField
                  label="Main image"
                  value={(field.value as string | null) ?? null}
                  onChange={(v) => field.onChange(v)}
                  kind="image"
                  folder="packages"
                  variant="square"
                />
              )}
            </UzFormField>
            <UzFormField name="bannerImageId">
              {({ field }) => (
                <MediaDropField
                  label="Banner image"
                  value={(field.value as string | null) ?? null}
                  onChange={(v) => field.onChange(v)}
                  kind="image"
                  folder="packages"
                  variant="banner"
                />
              )}
            </UzFormField>
            <UzFormField name="bannerVideoId">
              {({ field }) => (
                <MediaDropField
                  label="Banner video"
                  value={(field.value as string | null) ?? null}
                  onChange={(v) => field.onChange(v)}
                  kind="video"
                  folder="packages"
                  variant="banner"
                />
              )}
            </UzFormField>
          </>
        )}
      </FormSection>
      {pkg ? (
        <GalleryEditor pkg={pkg} />
      ) : (
        <GalleryEditor draftValue={galleryDraft} onDraftChange={onGalleryDraftChange} />
      )}
    </div>
  );
};
